import { useEffect, useState } from 'react';

export type VideoThumbStatus = 'loading' | 'ready' | 'failed';

export interface VideoThumbnailResult {
  /** Blob URL of the extracted middle-frame, or null until ready / on failure. */
  thumbnail: string | null;
  /** loading → ready | failed. */
  status: VideoThumbStatus;
}

/**
 * Captures a thumbnail from the middle frame of a video URL.
 *
 * Returns both the blob URL and a status so callers can render a
 * processing skeleton, the real frame, or a failure placeholder
 * (e.g. when the server hasn't generated a poster yet and CORS or
 * format prevents client-side extraction).
 *
 * For browsers + assets that support it this is "free" — but the
 * long-term answer is server-side poster generation on upload.
 */
export function useVideoThumbnail(videoUrl: string | undefined): VideoThumbnailResult {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [status, setStatus] = useState<VideoThumbStatus>('loading');

  useEffect(() => {
    if (!videoUrl) {
      setStatus('failed');
      setThumbnail(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    setStatus('loading');
    setThumbnail(null);

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    const fail = () => {
      if (cancelled) return;
      setStatus('failed');
      try { video.src = ''; } catch {}
    };

    const onSeeked = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext('2d');
        if (!ctx) return fail();
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (cancelled) return;
          if (!blob) return fail();
          objectUrl = URL.createObjectURL(blob);
          setThumbnail(objectUrl);
          setStatus('ready');
        }, 'image/jpeg', 0.85);
      } catch {
        fail();
      } finally {
        try { video.src = ''; } catch {}
      }
    };

    video.onloadedmetadata = () => {
      if (cancelled) return;
      // Seek to 50% — middle frame avoids a black first-frame on many phone clips.
      video.currentTime = video.duration > 0 ? video.duration * 0.5 : 0;
    };

    video.onseeked = onSeeked;
    video.onerror = fail;

    video.src = videoUrl;

    return () => {
      cancelled = true;
      try { video.src = ''; } catch {}
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [videoUrl]);

  return { thumbnail, status };
}
